import type { LogBindings, LogLevel } from "./index.js";

export type BrowserLoggerConfig = {
  endpoint?: string;
  maxBufferSize?: number;
  flushIntervalMs?: number;
  fetchFn?: typeof fetch;
  minLevel?: LogLevel;
  // Returns an Authorization header value (e.g. "Bearer <jwt>") to attach to
  // each flush request, or null/undefined to omit. Not applied to the
  // navigator.sendBeacon fallback, which cannot set custom headers.
  getAuthHeader?: () => string | null | undefined;
};

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

export type BrowserLogEntry = {
  level: LogLevel;
  msg: string;
  time: number;
  bindings: LogBindings;
  err?: { name: string; message: string; stack?: string };
};

type Payload = unknown;

export interface BrowserLogger {
  debug(obj: Payload, msg?: string): void;
  info(obj: Payload, msg?: string): void;
  warn(obj: Payload, msg?: string): void;
  error(obj: Payload, msg?: string): void;
  child(bindings: LogBindings): BrowserLogger;
  flush(): Promise<void>;
}

function serializeErr(err: unknown): BrowserLogEntry["err"] {
  if (!(err instanceof Error)) return undefined;
  return { name: err.name, message: err.message, stack: err.stack };
}

export function createBrowserLogger(config: BrowserLoggerConfig = {}): BrowserLogger {
  const endpoint = config.endpoint ?? "/internal/client-logs";
  const maxBufferSize = config.maxBufferSize ?? 50;
  const flushIntervalMs = config.flushIntervalMs ?? 5000;
  const fetchFn = config.fetchFn ?? (typeof fetch !== "undefined" ? fetch : undefined);
  const minLevel = LEVEL_ORDER[config.minLevel ?? "info"];
  const getAuthHeader = config.getAuthHeader;

  const buffer: BrowserLogEntry[] = [];
  let flushTimer: ReturnType<typeof setTimeout> | null = null;

  async function flush(): Promise<void> {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (buffer.length === 0) return;
    const entries = buffer.splice(0, buffer.length);
    if (!fetchFn) return;
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const authHeader = getAuthHeader?.();
    if (authHeader) headers.Authorization = authHeader;
    try {
      await fetchFn(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({ entries }),
        credentials: "include",
        keepalive: true,
      });
    } catch {
      // logging must never throw; drop the batch.
    }
  }

  function scheduleFlush(): void {
    if (flushTimer) return;
    flushTimer = setTimeout(flush, flushIntervalMs);
  }

  function makeLogger(ownBindings: LogBindings): BrowserLogger {
    function enqueue(level: LogLevel, payload: Payload, msg?: string): void {
      if (LEVEL_ORDER[level] < minLevel) return;
      let message = msg ?? "";
      let bindings: LogBindings = { ...ownBindings };
      let err: BrowserLogEntry["err"];

      if (typeof payload === "string") {
        message = payload;
      } else if (payload && typeof payload === "object") {
        const { err: errField, ...rest } = payload as Record<string, unknown>;
        bindings = { ...bindings, ...(rest as LogBindings) };
        err = serializeErr(errField);
      }

      buffer.push({ level, msg: message, time: Date.now(), bindings, err });

      if (buffer.length >= maxBufferSize) {
        void flush();
      } else {
        scheduleFlush();
      }
    }

    return {
      debug(obj, msg) {
        enqueue("debug", obj, msg);
      },
      info(obj, msg) {
        enqueue("info", obj, msg);
      },
      warn(obj, msg) {
        enqueue("warn", obj, msg);
      },
      error(obj, msg) {
        enqueue("error", obj, msg);
      },
      child(bindings) {
        return makeLogger({ ...ownBindings, ...bindings });
      },
      flush,
    };
  }

  if (typeof window !== "undefined") {
    const unloadFlush = (): void => {
      if (buffer.length === 0) return;
      const entries = buffer.splice(0, buffer.length);
      if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
        const blob = new Blob([JSON.stringify({ entries })], { type: "application/json" });
        navigator.sendBeacon(endpoint, blob);
      }
    };
    window.addEventListener("pagehide", unloadFlush);
    window.addEventListener("beforeunload", unloadFlush);
  }

  return makeLogger({});
}
